import {
  ExpressionValue,
  IElement,
  OneOfElements,
  OneOfPropertyElements,
} from '@shared/models/project.model';
import { IElementExpressionProperty } from '@shared/models/element-property.model';
import {
  getElementProperties,
  isElementExpressionProperty,
} from '../../element-property.helpers';
import { projectGuards } from '../../guards/project-guards.helpers';
import { isOneOfPropertyElements } from '../../recursive-element.helpers';
import { guards } from '../../typeguards.helpers';
import { ProjectChangeType } from '../update-engine.models';
import type { ProjectChangeMap } from '../update-engine.models';
import { ElementQuantityName } from '@shared/models/element-quantities.model';

type PropertyFormattedChange =
  ProjectChangeMap[ProjectChangeType.ElementPropertyFormatted];

/**
 * Build a partial element change for ElementPropertyFormatted.
 * Handles both regular properties and quantity properties.
 *
 * @param element - Current element state
 * @param change - Formatted value change payload
 * @returns Partial<OneOfElements> or undefined
 */
export const elementPropertyFormattedToChange = (
  element: OneOfElements,
  change: PropertyFormattedChange,
): Partial<OneOfElements> | undefined => {
  if (!isOneOfPropertyElements(element)) return undefined;

  const propChange = tryRegularPropertyFormatted(element, change);
  if (propChange) return propChange;

  if (!projectGuards.is.element(element)) return undefined;
  return tryQuantityPropertyFormatted(element, change);
};

const tryRegularPropertyFormatted = (
  element: OneOfPropertyElements,
  change: PropertyFormattedChange,
): Partial<OneOfElements> | undefined => {
  const props = getElementProperties(element);
  const idx = props.findIndex(
    (p) => isElementExpressionProperty(p) && p.name === change.propertyName,
  );
  if (idx === -1) return undefined;

  const prop = props[idx] as IElementExpressionProperty | undefined;
  if (!prop) return undefined;

  const countField = prop.count
    ? 'count'
    : prop.fallbackCount
      ? 'fallbackCount'
      : undefined;
  if (!countField) return undefined;

  const base = prop[countField] as ExpressionValue;
  if (base.formatted === change.formatted) return undefined;

  const newProps = [...props];
  newProps[idx] = {
    ...prop,
    [countField]: { ...base, formatted: change.formatted },
  };

  return { properties: newProps };
};

const tryQuantityPropertyFormatted = (
  element: IElement,
  change: PropertyFormattedChange,
): Partial<OneOfElements> | undefined => {
  if (!element.quantity) return undefined;

  const quantityName = change.propertyName as ElementQuantityName;
  const quantity = element.quantity[quantityName];
  if (!quantity) return undefined;

  const countField =
    'count' in quantity && quantity.count
      ? 'count'
      : 'fallbackCount' in quantity && quantity.fallbackCount
        ? 'fallbackCount'
        : undefined;
  if (!countField) return undefined;

  const existing = quantity[countField as keyof typeof quantity];
  if (!guards.is.object(existing)) return undefined;

  if ('formatted' in existing && existing.formatted === change.formatted) {
    return undefined;
  }

  return {
    quantity: {
      ...element.quantity,
      [quantityName]: {
        ...quantity,
        [countField]: { ...existing, formatted: change.formatted },
      },
    },
  };
};
